import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAddUserMutation } from "@/store/mover/apis";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { User } from "@/types";
import { formatPhoneNumber } from "@/lib/utils";
import TeamMember from "./components/TeamMember";
import { Link } from "react-router-dom";
import { Label } from "@/components/ui/label";

const Users: React.FC = () => {
  const dispatch = useDispatch();
  const selectedMover = useSelector(getSelectedMover);
  const { toast } = useToast();
  const [addUserApi, { isLoading }] = useAddUserMutation();
  const [newUser, setNewUser] = useState<User>({
    name: "",
    phone: "",
    email: "",
    roles: [],
    preferred_language: "English",
  });

  const addCrew = async (data: User) => {
    if (isLoading) {
      return;
    }

    if (!selectedMover) {
      return;
    }

    try {
      const invitedCrew = await addUserApi({
        data: {
          user: {
            ...(data.name?.split(" ")[0]
              ? { first_name: data.name.split(" ")[0] }
              : {}),
            ...(data.name?.split(" ")[1]
              ? { last_name: data.name.split(" ").slice(1).join(" ") }
              : {}),
            phone: data.phone,
            email: data.email,
            owner: data.roles?.includes("owner"),
            sales: data.roles?.includes("sales"),
            dispatcher: data.roles?.includes("dispatcher"),
            crew: false,
          },
        },
      }).unwrap();

      dispatch(
        updateSelectedMover({
          ...selectedMover,
          users: [...(selectedMover.users || []), invitedCrew],
        })
      );

      setNewUser({
        name: "",
        phone: "",
        email: "",
        roles: [],
        preferred_language: "English",
      });

      toast({
        title: "Success",
        description: "User added successfully.",
      });
    } catch (error) {
      console.error("Error adding user:", error);

      toast({
        title: "Error",
        description: "Failed to invite user. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRoleChange = (role: string, checked: boolean) => {
    setNewUser((prev) => {
      if (role === "owner" && checked) {
        return {
          ...prev,
          roles: ["owner"],
        };
      } else if (role === "owner" && !checked) {
        return {
          ...prev,
          roles: prev.roles?.filter((r) => r !== role) || [],
        };
      } else {
        const currentRoles = prev.roles || [];
        if (checked) {
          return {
            ...prev,
            roles: [...currentRoles, role],
          };
        } else {
          return {
            ...prev,
            roles: currentRoles.filter((r) => r !== role),
          };
        }
      }
    });
  };

  const isOwnerSelected = newUser.roles?.includes("owner");

  if (!selectedMover) {
    return null;
  }

  const sortedCrews = [...selectedMover.users]
    .filter((user) => user.owner || user.sales || user.dispatcher)
    .sort((a, b) => {
      return a.archived === b.archived ? 0 : a.archived ? 1 : -1;
    })
    .sort((a, b) => {
      return (
        new Date(a.created_at || "").getTime() -
        new Date(b.created_at || "").getTime()
      );
    });

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold">User Accounts</h2>
      <p className="text-sm">
        Manage user accounts for your office staff. To setup your crew,{" "}
        <Link
          className="font-semibold underline underline-offset-4 decoration-yellow-500 decoration-2"
          to="/mover-settings/crew"
        >
          click here
        </Link>
        .
      </p>
      <div className="mt-4 w-full flex flex-col justify-start items-start gap-4">
        <div className="w-full bg-stone-100 text-black px-2 py-1 text-sm">
          Invite New User
        </div>
        <div className="w-full grid grid-cols-12 gap-4 items-center">
          <div className="col-span-2">
            <Label className="text-sm font-semibold" htmlFor="name">
              Name
            </Label>
            <Input
              name="name"
              // placeholder="Name"
              value={newUser.name}
              required
              onChange={(e) =>
                setNewUser((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>
          <div className="col-span-3">
            <Label className="text-sm font-semibold" htmlFor="roles">
              Email
            </Label>
            <Input
              name="email"
              // placeholder="Email"
              value={newUser.email}
              required
              onChange={(e) =>
                setNewUser((prev) => ({
                  ...prev,
                  email: e.target.value,
                }))
              }
            />
          </div>
          <div className="col-span-2">
            <Label className="text-sm font-semibold" htmlFor="phone">
              Phone{" "}
              <span className="text-xs font-normal text-gray-700">
                (optional)
              </span>
            </Label>
            <Input
              name="phone"
              // placeholder="Phone"
              value={newUser.phone}
              required
              onChange={(e) =>
                setNewUser((prev) => ({
                  ...prev,
                  phone: formatPhoneNumber(e.target.value),
                }))
              }
            />
          </div>
          <div className="col-span-12">
            <div className="flex flex-row gap-8 items-start">
              <div className="flex flex-row justify-start items-start gap-6">
                <div>
                  <Label className="text-sm font-semibold" htmlFor="roles">
                    What is their role?
                  </Label>
                  <div className="text-xs text-gray-700">
                    (select all that apply)
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="owner"
                      checked={newUser.roles?.includes("owner")}
                      onCheckedChange={(checked) =>
                        handleRoleChange("owner", checked)
                      }
                    />
                    <div>
                      <label htmlFor="owner" className="font-medium text-sm">
                        Owner{" "}
                        <span className="text-sm text-gray-900 font-normal">
                          (full access)
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="sales"
                      checked={newUser.roles?.includes("sales")}
                      disabled={isOwnerSelected}
                      onCheckedChange={(checked) =>
                        handleRoleChange("sales", checked)
                      }
                    />
                    <div>
                      <label htmlFor="sales" className="font-medium text-sm">
                        Sales{" "}
                        <span className="text-sm text-gray-900 font-normal">
                          (access to everything except billing and accounting)
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="dispatcher"
                      checked={newUser.roles?.includes("dispatcher")}
                      disabled={isOwnerSelected}
                      onCheckedChange={(checked) =>
                        handleRoleChange("dispatcher", checked)
                      }
                    />
                    <div>
                      <label
                        htmlFor="dispatcher"
                        className="font-medium text-sm"
                      >
                        Operations/Dispatch{" "}
                        <span className="text-sm text-gray-900 font-normal">
                          (access to everything except billing and accounting)
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch id="crew" disabled={true} />
                    <div>
                      <label
                        htmlFor="crew"
                        className="font-medium text-sm text-gray-500"
                      >
                        Crew
                      </label>
                    </div>
                    <div className="text-sm">
                      (add your crew members{" "}
                      <Link
                        className="text-sm font-semibold underline underline-offset-4 decoration-yellow-500 decoration-2"
                        to="/mover-settings/crew"
                      >
                        here
                      </Link>
                      )
                    </div>
                  </div>

                  {/* <div className="flex items-center space-x-1">
                    <Switch
                      id="crew"
                      checked={newUser.roles?.includes("crew")}
                      disabled={isOwnerSelected}
                      onCheckedChange={(checked) =>
                        handleRoleChange("crew", checked)
                      }
                    />
                    <div>
                      <label htmlFor="crew" className="font-medium text-sm">
                        Crew
                      </label>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
          <div className="cursor-pointer col-span-6 flex items-center gap-1">
            <Button
              variant="default"
              type="button"
              onClick={() => {
                if (!newUser.name) {
                  alert("Please enter a name");
                  return;
                } else if (!newUser.email) {
                  alert("Please enter an email address");
                  return;
                } else if (newUser.roles?.length === 0) {
                  alert("Please select at least one role");
                  return;
                }

                addCrew({
                  name: newUser.name,
                  phone: newUser.phone,
                  email: newUser.email || "",
                  roles: newUser.roles,
                  preferred_language: "English",
                });
              }}
            >
              Invite User
            </Button>
          </div>
        </div>

        <div className="w-full flex flex-col gap-3">
          <div className="bg-stone-100 text-black px-2 py-1 text-sm">
            Active Users ({sortedCrews.length})
          </div>
          {sortedCrews.map((field) => (
            <TeamMember key={field.id} {...field} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Users;
